import { useRememberDocument } from '@/contexts/DocumentWorkspace';
import { useIncomingDocument } from '@/contexts/DocumentWorkspace';
import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  Upload,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  Loader2,
  CheckCircle,
  ShieldAlert,
  ShieldCheck,
  Download,
  X,
} from 'lucide-react';
import { pdfjsLib } from '@/lib/pdfWorker';
import { isPDFEncrypted, unlockPDF, formatFileSize } from '@/lib/pdfOperations';
import { unlockPDF as nativeUnlock, protectPDF as nativeProtect } from '@/lib/nativebridge';
import PageCanvas from '@/components/PageCanvas';
import { useFileDialog } from '@/hooks/useFileDialog';
import CloudSourceHint from '@/components/CloudSourceHint';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

/**
 * Protect — Password-protect or unlock PDFs.
 *
 * Protect tab: set a password + permissions (requires desktop app for encryption).
 * Unlock tab: remove password protection from an encrypted PDF.
 */
export default function Protect() {
  const navigate = useNavigate();
  const rememberDocument = useRememberDocument();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'protect';
  const { openFile, saveFile: saveFileDialog } = useFileDialog();
  // CloudSourceHint is imported below; wires into loadFile defined later.

  // --- Shared file state ---
  const [pdfDoc, setPdfDoc] = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // --- Protect tab state ---
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [allowPrinting, setAllowPrinting] = useState(true);
  const [allowCopying, setAllowCopying] = useState(true);
  const [allowEditing, setAllowEditing] = useState(false);
  const [protectedResult, setProtectedResult] = useState(null);

  // --- Unlock tab state ---
  const [isEncrypted, setIsEncrypted] = useState(null); // null = unknown, true/false
  const [unlockPassword, setUnlockPassword] = useState('');
  const [showUnlockPassword, setShowUnlockPassword] = useState(false);

  // --- Shared UI state ---
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  // ==================== File handling ====================

  const loadFile = useCallback(
    async (file) => {
      if (!file || file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) return;
      if (pdfDoc) pdfDoc.destroy();

      setError(null);
      setProtectedResult(null);
      setFileName(file.name);
      setFileSize(file.size);
      setIsEncrypted(null);
      setUnlockPassword('');

      const buf = await file.arrayBuffer();
      setArrayBuffer(buf);

      // Check encryption status
      try {
        const encrypted = await isPDFEncrypted(buf, pdfjsLib);
        setIsEncrypted(encrypted);

        if (!encrypted) {
          // Load for preview
          const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
          setPdfDoc(doc);
          rememberDocument(file);
          setNumPages(doc.numPages);
        } else {
          setPdfDoc(null);
          setNumPages(0);
        }
      } catch (err) {
        setError(`Failed to load PDF: ${err.message}`);
      }
    },
    [pdfDoc, rememberDocument]
  );

  const handleBrowse = useCallback(async () => {
    const result = await openFile('.pdf');
    if (!result) return;
    const file = new File([result.buffer], result.name, { type: 'application/pdf' });
    loadFile(file);
  }, [openFile, loadFile]);

  useIncomingDocument(loadFile);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    loadFile(e.dataTransfer.files[0]);
  }, [loadFile]);

  // ==================== Protect ====================

  const passwordsMatch = password.length > 0 && password === confirmPassword;

  const handleProtect = useCallback(async () => {
    if (!passwordsMatch || !arrayBuffer) return;

    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const result = await nativeProtect(arrayBuffer, {
        password,
        permissions: { print: allowPrinting, copy: allowCopying, edit: allowEditing },
      });

      if (!result) {
        // Desktop with no cloud + no local engine available.
        setError('Password protection needs an internet connection or the offline engine pack. Connect and try again.');
        return;
      }

      const bytes = new Uint8Array(result);
      setProtectedResult({ blob: new Blob([bytes], { type: 'application/pdf' }), size: bytes.length });
      if (usageCapApplies()) incrementUsage();
    } catch (err) {
      const msg = err.message || '';
      if (/signed in/i.test(msg)) {
        setError('Sign in (free) to password-protect PDFs — encryption runs on our secure server.');
      } else if (err.code === 'already_encrypted' || /already password-protected/i.test(msg)) {
        setError('This file is already password-protected. Unlock it first, then set a new password.');
      } else {
        setError(`Protection failed: ${msg}`);
      }
    } finally {
      setProcessing(false);
    }
  }, [passwordsMatch, arrayBuffer, password, allowPrinting, allowCopying, allowEditing]);

  const handleProtectDownload = useCallback(async () => {
    if (!protectedResult) return;
    const baseName = fileName.replace(/\.pdf$/i, '');
    const bytes = new Uint8Array(await protectedResult.blob.arrayBuffer());
    await saveFileDialog(bytes, `${baseName} (protected).pdf`);
  }, [protectedResult, fileName, saveFileDialog]);

  // ==================== Unlock ====================

  const handleUnlock = useCallback(async () => {
    if (!arrayBuffer || !unlockPassword) return;

    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }

    setProcessing(true);
    setProgress(0);
    setError(null);

    try {
      let bytes;

      // Try lossless unlock via qpdf (desktop native or cloud API)
      try {
        const nativeResult = await nativeUnlock(arrayBuffer, unlockPassword);
        if (nativeResult) {
          bytes = new Uint8Array(nativeResult);
        }
      } catch {
        // Cloud/native unavailable — fall through to browser unlock
      }

      // Browser fallback: rasterize-based unlock
      if (!bytes) {
        bytes = await unlockPDF(arrayBuffer, unlockPassword, pdfjsLib, setProgress);
      }

      const blob = new Blob([bytes], { type: 'application/pdf' });
      const baseName = fileName.replace(/\.pdf$/i, '');
      const file = new File([blob], `${baseName}_unlocked.pdf`, {
        type: 'application/pdf',
      });
      if (usageCapApplies()) incrementUsage();
      navigate('/viewer', { state: { file } });
    } catch (err) {
      const msg = err.message || '';
      if (
        msg.includes('password') ||
        msg.includes('Password') ||
        msg.includes('Incorrect Password') ||
        msg.includes('Incorrect password') ||
        err.name === 'PasswordException'
      ) {
        setError('Incorrect password — please try again');
      } else {
        setError(`Unlock failed: ${msg}`);
      }
      setProcessing(false);
    }
  }, [arrayBuffer, unlockPassword, fileName, navigate]);

  // ==================== Render ====================

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <h1 className="text-xl font-bold text-gray-900 flex-1">Protect & Unlock</h1>
        </div>
        <div className="max-w-3xl mx-auto flex gap-1 mt-3">
          <TabButton active={activeTab === 'protect'} onClick={() => { setSearchParams({ tab: 'protect' }); setError(null); setProtectedResult(null); }} icon={Lock}>Protect</TabButton>
          <TabButton active={activeTab === 'unlock'} onClick={() => { setSearchParams({ tab: 'unlock' }); setError(null); setProtectedResult(null); }} icon={Unlock}>Unlock</TabButton>
        </div>
      </div>

      <div className="max-w-3xl mx-auto w-full p-6">
        {/* Upload */}
        {!arrayBuffer ? (
          <>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onClick={handleBrowse}
              className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all mb-6 ${
                isDragging ? 'border-brand-blue bg-brand-light/30 scale-[1.01]' : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
              }`}
            >
              <Upload size={36} className={`mx-auto mb-3 ${isDragging ? 'text-brand-blue' : 'text-gray-400'}`} />
              <p className="text-lg font-medium text-gray-700">Drop a PDF here, or click to browse</p>
              <p className="text-sm text-gray-400 mt-1">
                {activeTab === 'protect' ? 'Add password protection to your PDF' : 'Remove password from a protected PDF'}
              </p>
            </div>
            <CloudSourceHint
              onPicked={(r) => loadFile(new File([r.buffer], r.name, { type: 'application/pdf' }))}
              className="mb-6"
            />
          </>
        ) : (
          /* File info */
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex items-center gap-3">
            {pdfDoc && (
              <div className="w-8 h-11 rounded border border-gray-200 overflow-hidden shrink-0 bg-white">
                <PageCanvas pdfDoc={pdfDoc} pageNumber={1} scale={0.08} />
              </div>
            )}
            {!pdfDoc && isEncrypted && (
              <div className="w-8 h-11 rounded border border-gray-200 shrink-0 bg-gray-100 flex items-center justify-center">
                <Lock size={14} className="text-gray-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
              <p className="text-xs text-gray-500">
                {numPages > 0 ? `${numPages} page${numPages !== 1 ? 's' : ''} · ` : ''}
                {formatFileSize(fileSize)}
                {isEncrypted && <span className="text-amber-600 ml-1">· Encrypted</span>}
              </p>
            </div>
            <button onClick={handleBrowse} className="text-sm text-brand-blue hover:underline font-medium">
              Change file
            </button>
          </div>
        )}

        {/* ==================== Protect tab ==================== */}
        {arrayBuffer && activeTab === 'protect' && !protectedResult && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {/* Password fields */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter a password"
                    className="w-full h-10 px-4 pr-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  className={`w-full h-10 px-4 text-sm border rounded-lg focus:outline-none focus:border-brand-blue ${
                    confirmPassword && !passwordsMatch ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {confirmPassword && !passwordsMatch && (
                  <p className="text-xs text-red-500 mt-1">Passwords don't match</p>
                )}
              </div>
            </div>

            {/* Advanced permissions */}
            <div className="mb-6">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {showAdvanced ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Advanced permissions
              </button>

              {showAdvanced && (
                <div className="mt-3 space-y-2.5 pl-1">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={allowPrinting} onChange={(e) => setAllowPrinting(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue" />
                    <span className="text-sm text-gray-700">Allow printing</span>
                  </label>
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={allowCopying} onChange={(e) => setAllowCopying(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue" />
                    <span className="text-sm text-gray-700">Allow copying text</span>
                  </label>
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={allowEditing} onChange={(e) => setAllowEditing(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue" />
                    <span className="text-sm text-gray-700">Allow editing</span>
                  </label>
                </div>
              )}
            </div>

            {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

            <button
              onClick={handleProtect}
              disabled={!passwordsMatch || processing}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Protecting…
                </>
              ) : (
                <>
                  <Lock size={16} />
                  Protect PDF
                </>
              )}
            </button>
          </div>
        )}

        {/* Protect success */}
        {activeTab === 'protect' && protectedResult && (
          <div className="bg-white rounded-xl border border-green-200 p-6 text-center">
            <ShieldCheck size={40} className="mx-auto mb-3 text-green-500" />
            <p className="text-lg font-semibold text-gray-900 mb-1">PDF protected with AES-256 encryption</p>
            <p className="text-sm text-gray-500 mb-6">{formatFileSize(protectedResult.size)}</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setProtectedResult(null)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleProtectDownload}
                className="flex items-center gap-2 px-5 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors"
              >
                <Download size={16} />
                Download
              </button>
            </div>
          </div>
        )}

        {/* ==================== Unlock tab ==================== */}
        {arrayBuffer && activeTab === 'unlock' && (
          <>
            {/* Not encrypted */}
            {isEncrypted === false && (
              <div className="bg-white rounded-xl border border-green-200 p-6 text-center">
                <CheckCircle size={40} className="mx-auto mb-3 text-green-500" />
                <p className="text-lg font-semibold text-gray-900 mb-1">No password protection</p>
                <p className="text-sm text-gray-500">
                  This PDF is not password-protected — no unlock needed.
                </p>
              </div>
            )}

            {/* Encrypted — password prompt */}
            {isEncrypted === true && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-5 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <ShieldAlert size={18} className="text-amber-600 shrink-0" />
                  <p className="text-sm text-amber-800">
                    This PDF is password-protected. Enter the password to unlock it.
                  </p>
                </div>

                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showUnlockPassword ? 'text' : 'password'}
                      value={unlockPassword}
                      onChange={(e) => { setUnlockPassword(e.target.value); setError(null); }}
                      onKeyDown={(e) => e.key === 'Enter' && unlockPassword && handleUnlock()}
                      placeholder="Enter PDF password"
                      className="w-full h-10 px-4 pr-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowUnlockPassword(!showUnlockPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showUnlockPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="text-red-600 text-sm mb-4 flex items-center gap-1.5">
                    <X size={14} />
                    {error}
                  </p>
                )}

                {processing && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Loader2 size={16} className="animate-spin text-brand-blue" />
                      <span className="text-sm text-gray-600">Unlocking…</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-brand-blue rounded-full transition-all duration-200" style={{ width: `${progress * 100}%` }} />
                    </div>
                  </div>
                )}

                <button
                  onClick={handleUnlock}
                  disabled={processing || !unlockPassword}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Unlock size={16} />
                  Unlock PDF
                </button>

              </div>
            )}
          </>
        )}
      </div>
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        active ? 'bg-brand-blue text-white' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <Icon size={16} />
      {children}
    </button>
  );
}
